import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import { title } from '@core/utils/filter'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { t } from '@/@core/libs/i18n/utils'

export default function useUsersList() {
  // Use toast
  const toast = useToast()

  const refUserListTable = ref(null)

  const msgUserList = {
    errorfetchUser: t('Error fetching {module} list', t('User')),
    name: t('Name'),
    email: t('Email'),
    role: t('Role'),
    plan: t('Plan'),
    status: t('Status'),
    actions: t('Actions'),
  }

  // Table Handlers
  const tableColumns = [
    { key: 'name', sortable: true, label: msgUserList.name },
    { key: 'email', sortable: true, label: msgUserList.email },
    { key: 'role', sortable: true, label: msgUserList.role },
    {
      key: 'currentPlan',
      label: msgUserList.plan,
      formatter: title,
      sortable: false,
    },
    { key: 'status', sortable: true, label: msgUserList.status },
    { key: 'actions', label: msgUserList.actions },
  ]

  const perPage = ref(10)
  const totalUsers = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [2, 5, 10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('name')
  const isSortDirDesc = ref(false)
  const roleFilter = ref(null)
  const planFilter = ref(null)
  const statusFilter = ref(null)

  const dataMeta = computed(() => {
    const localItemsCount = refUserListTable.value ? refUserListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalUsers.value,
    }
  })

  const refetchData = () => {
    refUserListTable.value.refresh()
  }

  watch([currentPage, perPage, searchQuery, roleFilter, planFilter, statusFilter], () => {
    refetchData()
  })

  const getUsers = (ctx, callback) => {
    const params = {
      q: searchQuery.value,
      perPage: perPage.value,
      page: currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      role: roleFilter.value,
      plan: planFilter.value,
      status: statusFilter.value,
    }
    store
      .dispatch('user-store/getUsers', params)
      .then(response => {
        const { users, total } = response.data
        callback(users)
        totalUsers.value = total
        localStorage.setItem('filterUserList', JSON.stringify(params))
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: msgUserList.errorfetchUser,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        },
        {
          position: 'bottom-right',
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = role => {
    if (role === 'subscriber') return 'primary'
    if (role === 'staff') return 'success'
    if (role === 'admin') return 'danger'
    if (role === 'merchant') return 'warning'
    return 'primary'
  }

  const resolveUserRoleIcon = role => {
    if (role === 'subscriber') return 'UserIcon'
    if (role === 'staff') return 'Edit2Icon'
    if (role === 'admin') return 'ServerIcon'
    if (role === 'merchant') return 'ShoppingBagIcon'
    return 'UserIcon'
  }

  const resolveUserStatusVariant = status => {
    if (status === 'pending') return 'warning'
    if (status === 'active') return 'success'
    if (status === 'inactive') return 'secondary'
    return 'primary'
  }

  const textFirstUpper = text => text.slice(0, 1).toUpperCase() + text.slice(1, text.length)

  return {
    getUsers,
    tableColumns,
    perPage,
    currentPage,
    totalUsers,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refUserListTable,

    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveUserStatusVariant,
    refetchData,
    textFirstUpper,

    // Extra Filters
    roleFilter,
    planFilter,
    statusFilter,
  }
}
